'use client';

import { useState, useCallback } from 'react';
import { ZodType, ZodError } from 'zod';
export interface InputLogicOptions<T> {
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  onValidateChange?: (value: T, error: string | null) => void;
  schema?: ZodType<T>;
}

export function useInputLogic<T>(options: InputLogicOptions<T>) {
  const { value, defaultValue, onChange, onValidateChange, schema } = options;

  const isControlled = value !== undefined;

  const [uncontrolledValue, setUncontrolledValue] = useState<T>(() => {
    const initial = (defaultValue ?? '') as T;
    return initial;
  });

  const [errorText, setErrorText] = useState<string | null>(null);

  const internalValue = isControlled ? (value as T) : uncontrolledValue;

  const validateValue = useCallback(
    (val: T): string | null => {
      if (!schema) return null;
      try {
        schema.parse(val);
        return null;
      } catch (err) {
        if (err instanceof ZodError) {
          return err.issues?.[0]?.message || 'Invalid value';
        }
        return 'Invalid value';
      }
    },
    [schema],
  );

  const emit = useCallback(
    (val: T) => {
      const err = validateValue(val);

      setErrorText(err);

      onChange?.(val);
      onValidateChange?.(val, err);
    },
    [onChange, onValidateChange, validateValue],
  );

  const handleChange = useCallback(
    (val: T) => {
      if (!isControlled) {
        setUncontrolledValue(val);
      }

      emit(val);
    },
    [isControlled, emit],
  );

  return {
    internalValue,
    handleChange,
    errorText,
  };
}
